import React, { useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Plus, Pencil, Search, Archive, Settings } from 'lucide-react';
import TemplateEditor from './TemplateEditor';

/**
 * TemplatesTab — admin-facing list of all in-line quality checksheet templates.
 * Shows one row per stage with quick metadata + Edit / Create actions.
 */
export function TemplatesTab({ templates, onReloaded }) {
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // template being edited (null = closed)
  const [showArchived, setShowArchived] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (templates || [])
      .filter((t) => showArchived || !t.archived)
      .filter((t) => !term
        || (t.stage_name || '').toLowerCase().includes(term)
        || (t.title_en || '').toLowerCase().includes(term)
        || (t.doc_no || '').toLowerCase().includes(term)
        || String(t.stage_index).includes(term))
      .sort((a, b) => (a.stage_index || 0) - (b.stage_index || 0));
  }, [templates, q, showArchived]);

  const archivedCount = useMemo(() => (templates || []).filter((t) => t.archived).length, [templates]);

  return (
    <div className="space-y-4" data-testid="iq-templates-tab">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-0 max-w-lg">
          <Search className="w-4 h-4 text-slate-400 shrink-0" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Search by stage name, doc no, or index…"
            data-testid="iq-templates-search" />
        </div>
        <div className="flex items-center gap-2">
          {archivedCount > 0 && (
            <button type="button" onClick={() => setShowArchived(!showArchived)}
              className="text-xs text-slate-600 hover:text-slate-800 underline"
              data-testid="iq-templates-toggle-archived">
              {showArchived ? 'Hide' : 'Show'} archived ({archivedCount})
            </button>
          )}
          <Button onClick={() => setCreateOpen(true)} data-testid="iq-templates-create">
            <Plus className="w-4 h-4 mr-1" />New template
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {filtered.map((t) => {
          const activeCharCount = (t.characteristics || []).filter((c) => !c.archived).length;
          const archivedCharCount = (t.characteristics || []).length - activeCharCount;
          return (
            <Card key={t.stage_index} className={`${t.archived ? 'opacity-70 border-dashed' : ''}`}
              data-testid={`iq-templates-card-${t.stage_index}`}>
              <CardContent className="p-4 space-y-2">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <div className="text-xs font-mono text-slate-400">#{t.stage_index}</div>
                      <div className="font-semibold text-slate-800 truncate">{t.stage_name}</div>
                    </div>
                    {t.title_en && <div className="text-[11px] text-slate-500 line-clamp-1">{t.title_en}</div>}
                  </div>
                  <button type="button" onClick={() => setEditing(t)}
                    className="shrink-0 p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-slate-800"
                    title="Edit template" data-testid={`iq-templates-edit-${t.stage_index}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-1.5 text-[10px]">
                  <Badge variant="outline">v{t.version || 1}</Badge>
                  <Badge variant="outline">{activeCharCount} field{activeCharCount === 1 ? '' : 's'}</Badge>
                  {archivedCharCount > 0 && (
                    <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">{archivedCharCount} archived</Badge>
                  )}
                  <Badge variant="outline">{t.frequency || 'Hourly'}</Badge>
                  {t.edited_by_admin && (
                    <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Custom</Badge>
                  )}
                  {t.archived && (
                    <Badge className="bg-slate-200 text-slate-700 hover:bg-slate-200">
                      <Archive className="w-3 h-3 mr-0.5" />Archived
                    </Badge>
                  )}
                </div>
                <div className="text-[10px] text-slate-400 flex items-center gap-1.5 flex-wrap">
                  {t.doc_no && <span>Doc {t.doc_no}</span>}
                  {t.edited_by_name && <span>· by {t.edited_by_name}</span>}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {filtered.length === 0 && (
          <div className="col-span-full text-center text-slate-500 py-10 border border-dashed border-slate-300 rounded-lg">
            <Settings className="w-8 h-8 mx-auto text-slate-300 mb-2" />
            No templates match your search. Click <span className="font-semibold">New template</span> to add one.
          </div>
        )}
      </div>

      <TemplateEditor
        open={!!editing || createOpen}
        initial={editing}
        onClose={() => { setEditing(null); setCreateOpen(false); }}
        onSaved={() => { onReloaded?.(); }}
        onArchived={() => { onReloaded?.(); }}
      />
    </div>
  );
}

export default TemplatesTab;
